import { MenuItemType } from '@/components/Menu/types'
import { PATHS_WITH_SECTIONS } from '@/router/RoutesUtils'
import { ReactNode } from 'react'
import InboundIcon from '@/components/Icons/InboundIcon'
import OutboundIcon from '@/components/Icons/OutboundIcon'
import SignedCasesIcon from '@/components/Icons/SignedCasesIcon'
import MyTasksIcon from '@/components/Icons/MyTasksIcon'
import { ReferralCategory } from '../types'

// Define navigation structure with routes and metadata
export interface MyReferralsNavItemType {
  id: 'my-tasks' | 'my-referrals-header' | 'inbound' | 'outbound' | 'signed' // Unique identifier used for routes and keys
  label: string // Display label for the menu
  title: string // Page title
  subtitle: string // Page subtitle/description
  route: string // URL path fragment (without base path)
  category?: ReferralCategory // Maps to ReferralCategory enum (optional for headers)
  testid?: string // Optional data-testid for automation tests
  icon?: ReactNode
  disabled?: boolean // If true, item is not clickable (used for headers/titles)
}

// Collection of all navigation items
export const MY_REFERRALS_NAV_ITEMS: MyReferralsNavItemType[] = [
  {
    id: 'my-tasks',
    label: 'My Tasks',
    route: 'my-tasks',
    title: 'My Tasks',
    subtitle: 'Manage your tasks and to-dos.',
    category: ReferralCategory.MY_TASKS,
    icon: <MyTasksIcon />,
  },
  {
    id: 'my-referrals-header',
    label: 'MY CASES',
    route: '',
    title: 'My Cases',
    subtitle: '',
    disabled: true,
  },
  {
    id: 'inbound',
    label: 'Inbound',
    route: 'inbound',
    title: 'Inbound Cases',
    subtitle: "Manage all cases you've received.",
    category: ReferralCategory.INBOUND,
    icon: <InboundIcon />,
  },
  {
    id: 'outbound',
    label: 'Outbound',
    route: 'outbound',
    title: 'Outbound Cases',
    subtitle: "Manage all cases you've sent.",
    category: ReferralCategory.OUTBOUND,
    icon: <OutboundIcon />,
  },
  {
    id: 'signed',
    label: 'Signed',
    route: 'signed',
    title: 'Signed Cases',
    subtitle: 'Track all signed clients.',
    category: ReferralCategory.SIGNED,
    icon: <SignedCasesIcon />,
  },
]

// Helper functions for the navigation system
export const getNavItemById = (
  id: MyReferralsNavItemType['id']
): MyReferralsNavItemType | undefined =>
  MY_REFERRALS_NAV_ITEMS.find(item => item.id === id)

export const getNavItemByRoute = (
  route: MyReferralsNavItemType['route']
): MyReferralsNavItemType | undefined =>
  MY_REFERRALS_NAV_ITEMS.find(item => item.route === route && !item.disabled)

export const getNavItemByCategory = (
  category: ReferralCategory
): MyReferralsNavItemType | undefined =>
  MY_REFERRALS_NAV_ITEMS.find(item => item.category === category)

// Map a /myReferrals/:id route segment to its category, respecting the
// My Tasks feature flag; undefined for unknown/disabled routes
export const getCategoryForRoute = (
  route: string | undefined,
  myTasksEnabled: boolean
): ReferralCategory | undefined => {
  if (!route) return undefined
  const item = getNavItemByRoute(route)
  if (!item?.category) return undefined
  if (item.id === 'my-tasks' && !myTasksEnabled) return undefined
  return item.category
}

export const getFullRoute = (item: MyReferralsNavItemType): string =>
  `${PATHS_WITH_SECTIONS.MY_REFERRALS}/${item.route}`

// Get default navigation item (first enabled item in the list)
export const getDefaultNavItem = (): MyReferralsNavItemType =>
  MY_REFERRALS_NAV_ITEMS.find(item => !item.disabled) ||
  MY_REFERRALS_NAV_ITEMS[0]

// Format the menu items for the sidebar navigation
export const menuItems: MenuItemType[] = [
  {
    sectionTitle: '',
    items: MY_REFERRALS_NAV_ITEMS.map(item => ({
      label: item.label,
      path: item.route,
      testid: item.testid,
      icon: item.icon,
      disabled: item.disabled,
    })),
  },
]
